import React, { useState } from "react";
import { useNotification } from "../../context/NotificationContext";
import {
  shuffleStateCharts,
  shuffleTop100Charts,
  updateStateCharts,
  updateTop100Charts,
  updateTrendingArtists,
  createNotificationDoc,
} from "../../services/AdminService";
import {
  IoRefresh,
  IoTrendingUp,
  IoShuffle,
  IoMegaphoneOutline,
} from "react-icons/io5";

type NotificationType = "top_100" | "new_music" | "new_artists";

export const AdminControlPanel = () => {
  const { showNotification } = useNotification();

  const [loadingState, setLoadingState] = useState({
    shuffleState: false,
    shuffleTop: false,
    updateState: false,
    updateTop: false,
    updateTrending: false,
    createNotification: false,
  });

  const [notificationType, setNotificationType] =
    useState<NotificationType>("top_100");
  const [messageText, setMessageText] = useState("");

  const handleAction = async (
    key: keyof typeof loadingState,
    fn: () => Promise<any>,
    successMsg: string,
  ) => {
    setLoadingState((prev) => ({ ...prev, [key]: true }));
    try {
      await fn();
      showNotification(successMsg);
    } catch (e) {
      console.error(e);
      showNotification("Action failed. Check developer console.");
    } finally {
      setLoadingState((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleCreateNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim()) {
      showNotification("Please enter a notification message.");
      return;
    }

    await handleAction(
      "createNotification",
      () => createNotificationDoc(messageText, notificationType),
      "Notification sent successfully!",
    );

    setMessageText("");
  };

  return (
    <>
      <div className="admin-section card-center">
        <h3 className="section-header">Update State Charts</h3>
        <p className="card-desc">Update state charts</p>
        <button
          className="btn-primary btn-full"
          onClick={() =>
            handleAction(
              "updateState",
              updateStateCharts,
              "State Charts Updated!",
            )
          }
          disabled={loadingState.updateState}
        >
          {loadingState.updateState ? (
            "Updating..."
          ) : (
            <>
              <IoRefresh style={{ marginRight: 8 }} /> Update
            </>
          )}
        </button>
      </div>

      <div className="admin-section card-center">
        <h3 className="section-header">Update Top 100</h3>
        <p className="card-desc">Update the Augio Top 100</p>
        <button
          className="btn-primary btn-full"
          onClick={() =>
            handleAction("updateTop", updateTop100Charts, "Top 100 Updated!")
          }
          disabled={loadingState.updateTop}
        >
          {loadingState.updateTop ? (
            "Updating..."
          ) : (
            <>
              <IoRefresh style={{ marginRight: 8 }} /> Update
            </>
          )}
        </button>
      </div>

      <div className="admin-section card-center">
        <h3 className="section-header">Update Trending Artists</h3>
        <p className="card-desc">Refresh the Trending Artists list</p>
        <button
          className="btn-primary btn-full"
          onClick={() =>
            handleAction(
              "updateTrending",
              updateTrendingArtists,
              "Trending Updated!",
            )
          }
          disabled={loadingState.updateTrending}
        >
          {loadingState.updateTrending ? (
            "Updating..."
          ) : (
            <>
              <IoTrendingUp style={{ marginRight: 8 }} /> Update
            </>
          )}
        </button>
      </div>

      <div className="admin-section card-center">
        <h3 className="section-header">Shuffle State Charts</h3>
        <p className="card-desc">Shuffle the order tracks in state charts</p>
        <button
          className="btn-primary btn-full"
          onClick={() =>
            handleAction(
              "shuffleState",
              shuffleStateCharts,
              "Shuffled State Charts!",
            )
          }
          disabled={loadingState.shuffleState}
        >
          {loadingState.shuffleState ? (
            "Shuffling..."
          ) : (
            <>
              <IoShuffle style={{ marginRight: 8 }} /> Shuffle
            </>
          )}
        </button>
      </div>

      <div className="admin-section card-center">
        <h3 className="section-header">Shuffle Top 100</h3>
        <p className="card-desc">Refresh the Top 100 list order</p>
        <button
          className="btn-primary btn-full"
          onClick={() =>
            handleAction("shuffleTop", shuffleTop100Charts, "Shuffled Top 100!")
          }
          disabled={loadingState.shuffleTop}
        >
          {loadingState.shuffleTop ? (
            "Shuffling..."
          ) : (
            <>
              <IoShuffle style={{ marginRight: 8 }} /> Shuffle
            </>
          )}
        </button>
      </div>

      <div className="admin-section full-width">
        <h3 className="section-header">Create Notification</h3>

        <form onSubmit={handleCreateNotification}>
          <div className="form-grid-2">
            <div className="form-group">
              <label>Notification Type</label>
              <select
                className="admin-select"
                value={notificationType}
                onChange={(e) =>
                  setNotificationType(e.target.value as NotificationType)
                }
              >
                <option value="top_100">Top 100</option>
                <option value="new_music">New Music</option>
                <option value="new_artists">New Artists</option>
              </select>
            </div>

            <div className="form-group">
              <label>Message Content</label>
              <textarea
                className="admin-input"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Enter custom broadcast text..."
                rows={1}
                style={{ resize: "vertical", minHeight: "45px" }}
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary btn-full"
            style={{ marginTop: "8px" }}
            disabled={loadingState.createNotification}
          >
            {loadingState.createNotification ? (
              "Creating..."
            ) : (
              <>
                <IoMegaphoneOutline style={{ marginRight: 8 }} /> Create
                Notification
              </>
            )}
          </button>
        </form>
      </div>
    </>
  );
};
